import { keepPreviousData, queryOptions, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAdminSocketEvent } from "@/lib/adminSocket";
import { STALE, unwrap } from "@/lib/query/queryClient";
import { listClinicianInvitationsApi, listCliniciansApi } from "./adminCliniciansApi";
import type { InvitationStatus } from "@/portals/clinician/api/types";
import { getPatientApi, listPatientsApi } from "./adminPatientsApi";
import { getPatientClinicianApi } from "./adminPatientClinicianApi";
import {
  getAdminQueueItemApi,
  getReviewQueueSummaryApi,
  listAdminReviewQueueApi,
  listAssignedItemsApi,
  type AdminItemState,
} from "./adminReviewQueueApi";

/** Query keys for the admin portal. */
export const adminKeys = {
  all: ["admin"] as const,
  /** Prefix: the queue list, its summary and every item detail. */
  reviewQueue: ["admin", "review-queue"] as const,
  reviewQueueList: ["admin", "review-queue", "list"] as const,
  reviewQueueSummary: ["admin", "review-queue", "summary"] as const,
  reviewQueueItem: (id: string) => ["admin", "review-queue", "item", id] as const,
  /** Prefix — one entry per filter. */
  assignedItems: ["admin", "assigned-items"] as const,
  clinicians: ["admin", "clinicians"] as const,
  /** Prefix — one entry per status filter. */
  invitations: ["admin", "invitations"] as const,
  /** Prefix — one entry per page + search. */
  patients: ["admin", "patients"] as const,
  patient: (patientId: string) => ["admin", "patient", patientId] as const,
  patientClinician: (patientId: string) => ["admin", "patient-clinician", patientId] as const,
};

/** The shared queue's summary — one fetch feeds the dashboard card and the nav badge. */
export const reviewQueueSummaryQuery = () =>
  queryOptions({
    queryKey: adminKeys.reviewQueueSummary,
    queryFn: () => unwrap(getReviewQueueSummaryApi()),
    staleTime: STALE.live,
  });

export const reviewQueueListQuery = () =>
  queryOptions({
    queryKey: adminKeys.reviewQueueList,
    queryFn: async () => (await unwrap(listAdminReviewQueueApi())).items,
    staleTime: STALE.live,
    meta: { errorToast: "Unable to load the review queue." },
  });

export const reviewQueueItemQuery = (id: string) =>
  queryOptions({
    queryKey: adminKeys.reviewQueueItem(id),
    queryFn: () => unwrap(getAdminQueueItemApi(id)),
    staleTime: STALE.live,
  });

export const assignedItemsQuery = (state: AdminItemState | undefined, clinicianUserId: string | undefined) =>
  queryOptions({
    queryKey: [...adminKeys.assignedItems, state ?? "ALL", clinicianUserId ?? ""] as const,
    queryFn: async () => (await unwrap(listAssignedItemsApi({ state, clinicianUserId }))).items,
    staleTime: STALE.live,
    // Switching filter keeps the current rows until the new ones land.
    placeholderData: keepPreviousData,
    meta: { errorToast: "Unable to load assigned items." },
  });

/** Shared by the Clinicians tab, both assign pickers and the assigned-items filter. */
export const cliniciansQuery = () =>
  queryOptions({
    queryKey: adminKeys.clinicians,
    queryFn: async () => (await unwrap(listCliniciansApi())).items,
    // Live, not slow: a clinician joins by accepting an invite in their own
    // browser, and nothing pushes that here, so a 5-minute copy left the
    // Clinicians tab and the assign pickers missing them while Invitations
    // already said "Accepted".
    staleTime: STALE.live,
    meta: { errorToast: "Unable to load clinicians." },
  });

export const invitationsQuery = (status: InvitationStatus | undefined) =>
  queryOptions({
    queryKey: [...adminKeys.invitations, status ?? "ALL"] as const,
    queryFn: async () => (await unwrap(listClinicianInvitationsApi(status))).items,
    staleTime: STALE.live,
    // A failed refresh behind a list that's already shown stays quiet — after a
    // failed resend/cancel the action's own toast is the one error the admin sees.
    meta: { errorToast: "Unable to load invitations.", errorToastWhenEmptyOnly: true },
  });

export const patientsQuery = (page: number, limit: number, search: string) =>
  queryOptions({
    queryKey: [...adminKeys.patients, page, search] as const,
    queryFn: () => unwrap(listPatientsApi({ page, limit, search: search || undefined })),
    staleTime: STALE.records,
    // Paging / searching keeps the current page on screen until the next one lands.
    placeholderData: keepPreviousData,
  });

export const patientQuery = (patientId: string) =>
  queryOptions({
    queryKey: adminKeys.patient(patientId),
    queryFn: () => unwrap(getPatientApi(patientId)),
    staleTime: STALE.records,
  });

export const patientClinicianQuery = (patientId: string) =>
  queryOptions({
    queryKey: adminKeys.patientClinician(patientId),
    queryFn: () => unwrap(getPatientClinicianApi(patientId)),
    staleTime: STALE.records,
  });

/** After an assign / reassign / return: every view of the queue and its items. */
export function invalidateReviewQueue(client: QueryClient): Promise<void> {
  return Promise.all([
    client.invalidateQueries({ queryKey: adminKeys.reviewQueue }),
    client.invalidateQueries({ queryKey: adminKeys.assignedItems }),
  ]).then(() => undefined);
}

/**
 * Admin realtime → cache. "review_queue:changed" (no data — something about
 * the queue or a held item changed) revalidates the queue, its summary, item
 * views and assigned items; an admin notification (e.g. a clinician
 * declining an invitation) revalidates invitations. useAdminSocketEvent
 * also calls each handler after a reconnect / server resync, when events may
 * have been missed. No-op under mocks.
 */
export function useAdminLiveQueries(): void {
  const client = useQueryClient();
  useAdminSocketEvent("review_queue:changed", () => void invalidateReviewQueue(client));
  useAdminSocketEvent("notification:new", () => {
    // An invite accepted / declined also changes who's on the clinicians list.
    void client.invalidateQueries({ queryKey: adminKeys.invitations });
    void client.invalidateQueries({ queryKey: adminKeys.clinicians });
  });
}
